import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import clsx from 'clsx'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import CalendarPriceBlurb from './CalendarPriceBlurb'
import { min } from 'lib/array/arrayUtils'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const Root = styled.div`
  padding-bottom: ${rem(32)};
`

const CalendarWrapper = styled.div`
  &:not(:first-child) {
    margin-top: ${rem(24)};
  }
`
const YearWrapper = styled.div`
  display: grid;
  margin-top: ${rem(20)};
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-gap: ${rem(20)};
    grid-template-columns: repeat(4, 1fr);
  }
`

const MonthTile = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  text-align: left;
  overflow: hidden;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

const BodyData = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: space-between;
  width: 100%;
  height: ${rem(60)};
`

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  margin-top: ${rem(8)};
`

interface Props {
  rooms?: Array<App.Occupants>
  className?: string
  selectedDay?: App.BedbankCalendarDay
  duration: number
  adultsNumber: number
  fetching: boolean
  flights: boolean
  displayPricingAsPerPerson: boolean
  onMonthSelect: (monthKey: string) => void
  monthsByYear: Map<string, Array<App.BedbankCalendarMonth>>
}

function CalendarMonthPicker(props: Props) {
  const {
    className,
    monthsByYear,
    onMonthSelect,
    duration,
    adultsNumber,
    selectedDay,
    fetching,
    flights,
    displayPricingAsPerPerson,
    rooms,
  } = props
  const intl = useIntl()

  const onMonthClick = useCallback((monthIndex: string) => {
    onMonthSelect(monthIndex)
  }, [onMonthSelect])

  const keyData = flights ? 'cheapestDayWithFlights' : 'cheapestDay'

  const yearCheapestDay = useMemo(() => {
    return Array.from(monthsByYear.entries()).reduce<Record<string, App.BedbankCalendarDay | undefined>>((acc, [year, months]) => {
      acc[year] = min(months.filter(d => d[keyData]), d => {
        const hotelPrice = d[keyData]?.price ?? 0
        const propertyFees = d[keyData]?.propertyFees ?? 0
        const hotelFlightPrice = d[keyData]?.flightPrice ?? 0
        return (hotelPrice + propertyFees) + (flights ? hotelFlightPrice : 0)
      })?.[keyData]
      return acc
    }, {})
  }, [monthsByYear, keyData, flights])

  return (
    <Root className={className}>
      {Array.from(monthsByYear.entries()).map(([year, months]) => {
        return <CalendarWrapper key={year}>
          <Heading variant="heading3">{year}</Heading>
          {!!yearCheapestDay[year] && !fetching && <CalendarPriceBlurb
            price={yearCheapestDay[year].price}
            propertyFees={yearCheapestDay[year].propertyFees}
            flightPrice={yearCheapestDay[year].flightPrice}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
          />}
          <YearWrapper>
            {months.map((month) => {
              const hotelPrice = month[keyData]?.price ?? 0
              const propertyFees = month[keyData]?.propertyFees ?? 0
              const {
                total,
                saleUnit,
              } = getCalendarDisplayTotalPrice({
                price: hotelPrice + propertyFees,
                adultsNumber,
                nights: duration,
                roomsNumber: rooms?.length ?? 1,
                withFlights: !!flights,
                flightPrice: month[keyData]?.flightPrice ?? 0,
                displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
              })

              return <MonthTile
                className={clsx({ selected: selectedDay?.monthKey === month.key })}
                onClick={() => onMonthClick(month.key)}
                key={month.key}
              >
                <BodyData>
                  <div>
                    <Heading variant="heading6">{month.month}</Heading>
                    {fetching && <VerticalSpacer gap={4}>
                      <TextLoadingBox typography="caption-large" width="100%" />
                      <TextLoadingBox typography="body-large" width="100%" />
                    </VerticalSpacer>}
                    {!fetching && <MainSection>
                      {!month[keyData] && <Caption variant="large" colour="neutral-three" underline>
                        {intl.formatMessage({
                          id: 'hgJJUFUUf4',
                          defaultMessage: 'View dates',
                          description: 'Link text to view available dates for a month on the calendar picker',
                        })}
                      </Caption>}
                      {month[keyData] && month.hasPromo &&
                        <div>
                          <Caption variant="medium" colour="neutral-three">{pluralizeToString('night', duration)} {flights && month[keyData]?.flightPrice && '+ flights '} from</Caption>
                          <BodyText as="span" variant="large" weight="bold">
                            <FormatCurrency value={total} />
                            {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                          </BodyText>{' '}
                          {!displayPricingAsPerPerson && <><Caption as="span" variant="medium" colour="neutral-three">{saleUnit}</Caption>
                            {flights && !month[keyData]?.flightPrice &&
                              <Caption as="span" variant="medium" colour="neutral-three">
                                {' '} + flights
                              </Caption>}
                          </>}
                        </div>}
                    </MainSection>}
                  </div>
                  <div>
                    {month.hasPromo && <SolidFireIcon size="S" colour="urgency" />}
                  </div>
                </BodyData>
              </MonthTile>
            })}
          </YearWrapper>
        </CalendarWrapper>
      })}
    </Root>
  )
}

export default CalendarMonthPicker
